// WallUser.jsx - Shared component for displaying user in wall view
import anime from 'animejs';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { UserName } from './UserName.jsx';

// Keep in sync with app/imports/api/voting/wallVotingLabels.js — a quiz's
// correct-answer wall post carries this sentinel prefix in userName so we render
// the check-fat icon + "Korrekte Antwort" instead of the rank + trophy. MM #189.
const CORRECT_ANSWER_USERNAME_PREFIX = 'correct::';
const isCorrectAnswerName = (userName) =>
  typeof userName === 'string' &&
  userName.startsWith(CORRECT_ANSWER_USERNAME_PREFIX);
const wallDisplayName = (userName) =>
  (isCorrectAnswerName(userName)
    ? userName.slice(CORRECT_ANSWER_USERNAME_PREFIX.length)
    : userName
  )
    ?.replace(/^real\s+/i, '')
    .replace(/_/g, '');

export const WallUser = ({ wallData = {}, renderEmblem = null }) => {
  const wallFilterColor = 'filter-(--filter-white)';
  const wallDropShadowStyle =
    wallData?.wallSettings?.dropShadowStyle ||
    'drop-shadow-[0_0_1rem_rgba(0,0,0,0.25)]';
  const isWallOnly = wallData?.isWallOnly || false;
  const isLandscapeOnly = wallData?.isLandscapeOnly || false;
  const addDebugStyle = wallData?.addDebugStyle || '';
  const usedSize = 'wall';

  const messageTypeCurrent =
    wallData?.wallMessage?.postItem?.postTypeForWall || 'none';
  const wallTextColor = 'text-white';
  const transitionDuration = 'duration-250';

  // Hide user when backgroundOnly is true (for QR code displays)
  const backgroundOnly = wallData?.wallMessage?.postItem?.backgroundOnly ?? false;
  const userShow = backgroundOnly
    ? false
    : (wallData?.wallSettings?.wallUser?.userShow ?? true);
  const messageFadeDuration =
    wallData?.wallSettings?.wallMessage?.messageFadeDuration || 500;
  const userEffectTranslateYIn =
    wallData?.wallSettings?.wallUser?.userEffectTranslateYIn || '0';
  const userEffectTranslateYOut =
    wallData?.wallSettings?.wallUser?.userEffectTranslateYOut || '0';
  const userEffectTranslateXIn =
    wallData?.wallSettings?.wallUser?.userEffectTranslateXIn || '0';
  const userEffectTranslateXOut =
    wallData?.wallSettings?.wallUser?.userEffectTranslateXOut || '0';
  const userEffectScaleIn =
    wallData?.wallSettings?.wallUser?.userEffectScaleIn || '1';
  const userEffectScaleOut =
    wallData?.wallSettings?.wallUser?.userEffectScaleOut || '1';
  const userEffectBlur =
    wallData?.wallSettings?.wallUser?.userEffectBlur || '0';
  const userEffectEaseIn =
    wallData?.wallSettings?.wallUser?.userEffectEaseIn || 'easeOutSine';
  const userEffectEaseOut =
    wallData?.wallSettings?.wallUser?.userEffectEaseOut || 'easeOutSine';

  const [user1, setUser1] = useState(null);
  const [user2, setUser2] = useState(null);
  const [type1, setType1] = useState(false);
  const [type2, setType2] = useState(false);
  const [isUser1Visible, setIsUser1Visible] = useState(false);

  const refUser1 = useRef(null);
  const refUser2 = useRef(null);
  const animeScope = useRef(null);

  useEffect(() => {
    // Use postTypeForWall for correct type identification (votes have type='snap' but postTypeForWall='vote')
    // Note: backgroundOnly hiding is now handled via userShow opacity transition
    const postItem = wallData?.wallMessage?.postItem;

    if (isUser1Visible) {
      setUser2(postItem);
      setType2(postItem?.postTypeForWall);
    } else {
      setUser1(postItem);
      setType1(postItem?.postTypeForWall);
    }
    setIsUser1Visible((oldValue) => !oldValue);
  }, [
    wallData?.wallMessage?.postItem?.userName,
    wallData?.wallMessage?.postItem?.postTypeForWall,
    backgroundOnly,
  ]);

  useLayoutEffect(() => {
    // Only run crossfade when both users exist
    if (!user1 || !user2) return;

    const inUser = isUser1Visible ? refUser1.current : refUser2.current;
    const outUser = isUser1Visible ? refUser2.current : refUser1.current;

    if (!inUser) return;

    if (animeScope.current) {
      animeScope.current.pause();
    }

    inUser.style.opacity = '0';
    animeScope.current = anime({
      targets: inUser,
      opacity: 1,
      translateY: [userEffectTranslateYIn, '0'],
      translateX: [userEffectTranslateXIn, '0'],
      scale: [userEffectScaleIn, 1],
      filter: ['blur(' + userEffectBlur + ')', 'blur(0px)'],
      easing: userEffectEaseIn,
      duration: messageFadeDuration / 2,
      delay: messageFadeDuration / 4,
      begin: () => {
        if (outUser) {
          anime({
            targets: outUser,
            opacity: 0,
            translateY: ['0', userEffectTranslateYOut],
            translateX: ['0', userEffectTranslateXOut],
            scale: [1, userEffectScaleOut],
            filter: ['blur(0px)', 'blur(' + userEffectBlur + ')'],
            easing: userEffectEaseOut,
            duration: messageFadeDuration / 2,
            delay: messageFadeDuration / 4,
            complete: () => {
              if (outUser) outUser.style.opacity = '0';
            },
          });
        }
      },
      complete: () => {
        if (inUser) inUser.style.opacity = '1';
      },
    });
  }, [isUser1Visible, user1, user2]);

  const wallUserStyle =
    `z-30 absolute flex items-center transition-all ${userShow ? "opacity-100" : "opacity-0"} ` + transitionDuration;
  const wallUserLayoutStyle =
    messageTypeCurrent == 'post' ||
    messageTypeCurrent == 'snap' ||
    messageTypeCurrent == 'feel'
      ? isWallOnly
        ? 'top-23/32 h-1/16 left-1/2 w-14/16 -translate-x-1/2 justify-center'
        : isLandscapeOnly
          ? 'top-14/16 h-1/16 left-1/32 w-9/16 translate-x-0 justify-left'
          : 'top-12/16 h-1/16 left-2/16 w-12/16 -translate-x-0 justify-center'
      : messageTypeCurrent == 'vote'
        ? isWallOnly
          ? 'top-23/32 h-1/16 left-1/32 w-28/32 -translate-x-0 justify-left'
          : isLandscapeOnly
            ? 'top-14/16 h-1/16 left-1/32 w-9/16 -translate-x-0 justify-left'
            : 'top-12/16 h-1/16 left-layout w-12/16 -translate-x-0 justify-left'
        : '';
  const wallUserSubStyle = 'absolute max-w-full flex flex-col';
  const wallUserSubLayoutStyle =
    messageTypeCurrent == 'post' ||
    messageTypeCurrent == 'snap' ||
    messageTypeCurrent == 'feel'
      ? isWallOnly
        ? 'items-center'
        : isLandscapeOnly
          ? 'items-start'
          : 'items-center'
      : messageTypeCurrent == 'vote'
        ? isWallOnly
          ? 'items-start'
          : isLandscapeOnly
            ? 'items-start'
            : 'items-start'
        : '';
  const wallUserIconContainerStyle =
    'absolute transition-all ' + transitionDuration;
  const wallUserIconContainerLayoutStyle =
    messageTypeCurrent == 'post' || messageTypeCurrent == 'feel'
      ? isWallOnly
        ? 'top-full translate-y-2 opacity-0'
        : isLandscapeOnly
          ? '-top-2 -translate-y-full opacity-0'
          : 'top-full translate-y-1 opacity-0'
      : messageTypeCurrent == 'snap'
        ? isWallOnly
          ? 'top-full translate-y-2 opacity-100'
          : isLandscapeOnly
            ? '-top-2 -translate-y-full opacity-100'
            : 'top-full translate-y-1 opacity-100'
        : messageTypeCurrent == 'vote'
          ? isWallOnly
            ? 'top-full translate-y-2 opacity-100'
            : isLandscapeOnly
              ? '-top-2 -translate-y-full opacity-100'
              : 'top-full translate-y-1 opacity-100'
          : '';
  const wallUserIconStyle = isWallOnly
    ? ['size-[5vmin] min-w-8 min-h-8', wallFilterColor].join(' ')
    : isLandscapeOnly
      ? ['size-8 sm:size-10', wallFilterColor].join(' ')
      : ['size-6 xs:size-8 sm:size-10', wallFilterColor].join(' ');

  return (
    <div
      id="wallUser"
      className={[
        wallUserStyle,
        wallUserLayoutStyle,
        wallDropShadowStyle,
        addDebugStyle,
      ].join(' ')}
    >
      <div
        id="wallUser1"
        ref={refUser1}
        className={[wallUserSubStyle, wallUserSubLayoutStyle].join(' ')}
        style={{ willChange: 'opacity, transform' }}
        data-starting-number={user1?.startingNumber || ''}
        data-post-type={type1 || ''}
      >
        <div
          key="icon"
          className={[
            wallUserIconContainerStyle,
            wallUserIconContainerLayoutStyle,
          ].join(' ')}
        >
          {type1 === 'vote' && (
            <img
              className={wallUserIconStyle}
              src={
                isCorrectAnswerName(user1?.userName)
                  ? '/media/icons/ph-regular/check-fat.svg'
                  : '/media/icons/ph-regular/trophy.svg'
              }
              alt=""
            />
          )}
        </div>

        <div key="name">
          <UserName
            size={usedSize}
            name={wallDisplayName(user1?.userName)}
            nameStyle={wallTextColor}
            verification={user1?.userName?.startsWith('real')}
            hide={user1?.userName?.startsWith('unreal')}
            verificationFilter={wallFilterColor}
            emblem={user1?.emblem}
            emblemUrl={user1?.emblemUrl}
            renderEmblem={renderEmblem}
            truncate="truncate"
            data-starting-number={user1?.startingNumber || ''}
            data-starter-name={user1?.starterName || ''}
            data-starter-id={user1?.starterId || ''}
          />
        </div>
      </div>

      <div
        id="wallUser2"
        ref={refUser2}
        className={[wallUserSubStyle, wallUserSubLayoutStyle].join(' ')}
        style={{ willChange: 'opacity, transform' }}
        data-starting-number={user2?.startingNumber || ''}
        data-post-type={type2 || ''}
        >
        <div
          key="icon"
          className={[
            wallUserIconContainerStyle,
            wallUserIconContainerLayoutStyle,
          ].join(' ')}
        >
          {type2 === 'vote' && (
            <img
              className={wallUserIconStyle}
              src={
                isCorrectAnswerName(user2?.userName)
                  ? '/media/icons/ph-regular/check-fat.svg'
                  : '/media/icons/ph-regular/trophy.svg'
              }
              alt=""
            />
          )}
        </div>

        <div>
          <UserName
            size={usedSize}
            name={wallDisplayName(user2?.userName)}
            nameStyle={wallTextColor}
            verification={user2?.userName?.startsWith('real')}
            hide={user2?.userName?.startsWith('unreal')}
            verificationFilter={wallFilterColor}
            emblem={user2?.emblem}
            emblemUrl={user2?.emblemUrl}
            renderEmblem={renderEmblem}
            truncate="truncate"
            data-starting-number={user2?.startingNumber || ''}
            data-starter-name={user2?.starterName || ''}
            data-starter-id={user2?.starterId || ''}
          />
        </div>
      </div>
    </div>
  );
};

export const areWallUserPropsEqual = (prevProps, nextProps) => {
  const relevantPrevProps = {
    userName: prevProps.wallData?.wallMessage?.postItem?.userName,
    postTypeForWall: prevProps.wallData?.wallMessage?.postItem?.postTypeForWall,
    backgroundOnly: prevProps.wallData?.wallMessage?.postItem?.backgroundOnly,
    // #248: re-render live on any client-settings change.
    wallSettings: prevProps.wallData?.wallSettings,
  };

  const relevantNextProps = {
    userName: nextProps.wallData?.wallMessage?.postItem?.userName,
    postTypeForWall: nextProps.wallData?.wallMessage?.postItem?.postTypeForWall,
    backgroundOnly: nextProps.wallData?.wallMessage?.postItem?.backgroundOnly,
    wallSettings: nextProps.wallData?.wallSettings,
  };

  return (
    JSON.stringify(relevantPrevProps) === JSON.stringify(relevantNextProps)
  );
};

export const WallUserMemo = React.memo(WallUser, areWallUserPropsEqual);
